// ui/data/invalidation.js — every successful WRITE marks the cached reads it
// affects as stale, wherever it was made from.
//
// services/http.js calls `invalidateForWrite(config)` after each successful
// non-GET response. That is the safety net behind the explicit invalidations
// the pages do after their own actions: a write from anywhere (a legacy page,
// a drawer, a wizard, a research "Save as draft") can never leave another
// page's cached copy stale. Active queries re-read at once; inactive ones on
// their next mount.
//
// Only real writes are listed. POSTs that are reads or computations
// (/api/crypto_price, universe preview, sweep plan, explain, AI generate, a
// rebalance DRY RUN, …) match nothing and touch nothing.
import { qk } from './keys';
import { appQueryClient } from './queryClient';

const exposure = ['portfolio', 'exposure'];

/** [method (or '*'), path test, keys to invalidate]. First match wins. */
export const WRITE_RULES = [
  ['*', /\/api\/allocation\/rebalance(\?|$)/, [qk.allocation, qk.bots]],
  ['put', /\/api\/allocation(\?|$)/, [qk.allocation]],
  ['*', /\/api\/risk\/flatten(\?|$)/, [qk.risk, qk.portfolio, qk.bots]],
  ['*', /\/api\/risk\/limits(\?|$)/, [qk.risk, exposure]],
  ['*', /\/api\/risk\/(halt|resume)(\/global)?(\?|$)/, [qk.risk]],
  ['*', /\/api\/scanner\/profiles\/[^/]+\/unpause/, [qk.scanners, qk.bots]],
  ['*', /\/api\/trading_bots(\/|\?|$)/, [qk.bots, qk.scanners, qk.allocation]],
  ['*', /\/api\/bots\//, [qk.bots, qk.scanners]], // create, redeploy a version, waive the report card
  // A draft / scanner book test: the draft list AND the Running list.
  ['post', /\/api\/strategy-drafts\/[^/?]+\/backtest(\?|$)/, [qk.drafts, qk.backtestRunning]],
  ['*', /\/api\/strategy-drafts(\/|\?|$)/, [qk.drafts]],
  ['*', /\/api\/(add_exchange|exchanges?)(\/|\?|$)/, [qk.exchanges, qk.portfolio, qk.alpacaStatus]],
  ['*', /\/api\/assets(\/|\?|$)/, [qk.portfolio]],
  ['*', /\/api\/backtest\/[^/]+\/publish(\?|$)/, [qk.backtest, qk.research]],
  ['*', /\/api\/backtest\/(save|cancel)(\/|\?|$)/, [qk.backtest]],
  ['post', /\/api\/backtest(\?|$)/, [qk.backtestActive, qk.backtestRunning]], // a new run
  ['post', /\/api\/backtest\/(sweep|walkforward)(\?|$)/, [qk.backtestRunning]],
  ['delete', /\/api\/backtest\/[^/?]+(\?|$)/, [qk.backtest]],
  // Follows mark rows in the four Who's-buying feeds (useResearchQuery names).
  ['*', /\/api\/people\/follows(\/|\?|$)/, [
    [...qk.research, 'peopleManagers'], [...qk.research, 'peopleInsiders'],
    [...qk.research, 'peoplePoliticians'], [...qk.research, 'peopleFunds'],
  ]],
];

const bodyOf = (config) => {
  const d = config && config.data;
  if (!d) return {};
  if (typeof d === 'object') return d;
  try { return JSON.parse(d); } catch { return {}; }
};

/** The keys a successful request invalidates (none for reads). Pure — see invalidation.test.js. */
export function keysForWrite(config = {}) {
  const method = String(config.method || 'get').toLowerCase();
  if (method === 'get' || method === 'head' || method === 'options') return [];
  const url = String(config.url || '');
  // A dry-run rebalance only previews; nothing moved.
  if (/\/api\/allocation\/rebalance/.test(url) && bodyOf(config).dryRun === true) return [];
  const rule = WRITE_RULES.find(([m, re]) => (m === '*' || m === method) && re.test(url));
  return rule ? rule[2] : [];
}

export function invalidateForWrite(config, client = appQueryClient) {
  keysForWrite(config).forEach((queryKey) => {
    client.invalidateQueries({ queryKey });
  });
}
